/**
 * sessionStorage round-trip for the Stripe OAuth "return to editor" UX.
 *
 * Why: when the user clicks "Connect Stripe" inside the paywall product
 * picker, we do a full-page redirect to Stripe. After OAuth completes,
 * the backend redirects to a HARDCODED URL: `/project/{pid}/integrations`.
 * That's the wrong place if the user started from the screen editor.
 *
 * Solution: before redirecting, store the editor URL here. A global
 * `StripeOAuthReturnHandler` reads this on next app mount and navigates
 * back via React Router. No backend changes required.
 *
 * Storage shape:
 *   {
 *     url: string,            // editor URL, e.g. '/project/abc/onboarding-v2/xyz'
 *     stepId?: string,        // optional, for reopening the screen editor
 *     rowIndex?: number,      // optional, which plan row triggered the connect
 *     mode: 'test' | 'live',
 *     startedAt: number,      // Date.now() at redirect
 *     expiresAt: number,      // startedAt + ttlMs (10 min by default)
 *   }
 *
 * Defensive reads: TTL expiry, JSON parse errors, invalid URL shape, and
 * missing required fields all return null (and clear the bad entry).
 */

export const OAUTH_RETURN_STORAGE_KEY = 'segmently_stripe_oauth_return';
// 30 minutes — Stripe Connect Standard new-account onboarding (KYC,
// business details, identity verification) often runs 10-20 minutes.
// Originally 10 min in M7 design, bumped after the first manual test
// showed the entry expiring before the user finished OAuth.
export const DEFAULT_OAUTH_RETURN_TTL_MS = 30 * 60 * 1000;

export interface OAuthReturnEntry {
  url: string;
  stepId?: string;
  rowIndex?: number;
  mode: 'test' | 'live';
  startedAt: number;
  expiresAt: number;
}

export interface SetReturnUrlInput {
  url: string;
  mode: 'test' | 'live';
  stepId?: string;
  rowIndex?: number;
  ttlMs?: number;
}

/**
 * Write the return entry to sessionStorage. Caller should invoke right
 * before `window.location.href = oauthUrl` so the entry is in place when
 * the user returns.
 */
export function setReturnUrl(input: SetReturnUrlInput): void {
  const now = Date.now();
  const entry: OAuthReturnEntry = {
    url: input.url,
    stepId: input.stepId,
    rowIndex: input.rowIndex,
    mode: input.mode,
    startedAt: now,
    expiresAt: now + (input.ttlMs ?? DEFAULT_OAUTH_RETURN_TTL_MS),
  };
  try {
    sessionStorage.setItem(OAUTH_RETURN_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage may be unavailable (private browsing, quota exceeded).
    // The picker still continues to redirect; the user will simply land
    // on the Integrations page (the default backend behavior).
  }
}

/**
 * Read + validate the return entry. Returns null on any of: missing,
 * malformed JSON, expired (now > expiresAt), missing required fields,
 * or URL fails path-shape check.
 *
 * Stale/invalid entries are cleared as a side effect to keep the key
 * tidy across navigations.
 */
export function getReturnUrl(): OAuthReturnEntry | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(OAUTH_RETURN_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Corrupted entry — clear it so the next read is a clean miss.
    clearReturnUrl();
    return null;
  }

  if (!isOAuthReturnEntry(parsed)) {
    clearReturnUrl();
    return null;
  }

  if (Date.now() > parsed.expiresAt) {
    clearReturnUrl();
    return null;
  }

  if (!isValidEditorPath(parsed.url)) {
    clearReturnUrl();
    return null;
  }

  return parsed;
}

/**
 * Trigger the actual Stripe OAuth redirect. Wrapped here so callers can
 * mock a single function in tests instead of stubbing `window.location`
 * — jsdom makes both `location.href` (non-configurable setter) and
 * `location.assign` (read-only method) impossible to spy on directly.
 *
 * In production this is `window.location.assign(url)` — full-page navigation
 * to the Stripe OAuth authorize page.
 */
export function redirectToOAuth(url: string): void {
  // assign() over href= is identical browser semantics but slightly easier
  // to reason about in tests; both result in a full document navigation.
  window.location.assign(url);
}

export function clearReturnUrl(): void {
  try {
    sessionStorage.removeItem(OAUTH_RETURN_STORAGE_KEY);
  } catch {
    // No-op on storage failure — best-effort cleanup.
  }
}

/**
 * Shape guard. We're reading untrusted JSON from sessionStorage; another
 * code path could in theory clobber the key with garbage. Validate every
 * required field's type before trusting the entry.
 */
function isOAuthReturnEntry(value: unknown): value is OAuthReturnEntry {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.url === 'string' &&
    (v.mode === 'test' || v.mode === 'live') &&
    typeof v.startedAt === 'number' &&
    typeof v.expiresAt === 'number' &&
    (v.stepId === undefined || typeof v.stepId === 'string') &&
    (v.rowIndex === undefined || typeof v.rowIndex === 'number')
  );
}

/**
 * Defense-in-depth: only accept URLs that look like a Segmently project
 * route. Prevents an attacker (or buggy code) from writing an absolute
 * URL into sessionStorage and getting us to navigate off-domain.
 */
function isValidEditorPath(url: string): boolean {
  if (!url.startsWith('/project/')) return false;
  // Reject explicit scheme/host attempts even when prefixed with /project/
  if (url.includes('://')) return false;
  // Reject path traversal sequences
  if (url.includes('..')) return false;
  return true;
}
